import { useCallback, useEffect, useState } from 'react';
import { fetchSummary, type AnalyticsSummary, type Range } from './analyticsApi';
import StatTile from './charts/StatTile';
import TrendChart from './charts/TrendChart';
import FunnelChart from './charts/FunnelChart';
import TopArticlesTable from './charts/TopArticlesTable';
import { percent } from './charts/format';

const RANGES: { key: Range; label: string }[] = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

// Umami's session salt rotates monthly, so a 90-day figure counts visitors per
// month and cannot be summed across them.
const MONTHLY_FOOTNOTE = 'Counted per month; not summable across months';

export default function StatsPage() {
  const [range, setRange] = useState<Range>('30d');
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (which: Range) => {
    setLoading(true);
    setFailed(false);
    try {
      setData(await fetchSummary(which));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(range);
  }, [load, range]);

  if (failed) {
    return (
      <p className="font-nunito text-sm text-danger">
        Could not load the stats. Try again shortly.
      </p>
    );
  }

  if (loading && !data) {
    return <p className="font-nunito text-sm text-meta">Loading stats…</p>;
  }

  if (!data) return null;

  const visitors = data.trend.map((p) => p.visitors);
  const pageviews = data.trend.map((p) => p.pageviews);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        {RANGES.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setRange(key)}
            aria-pressed={range === key}
            className={`rounded-full px-4 py-1.5 font-nunito text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-lime-600/40 ${
              range === key ? 'bg-cream text-lime-700' : 'text-gray-600 hover:text-lime-700'
            }`}
          >
            {label}
          </button>
        ))}

        {/* Umami is down and these are the last good numbers — a note, not an
            error: slightly-old numbers still answer the question. */}
        {data.stale && (
          <span className="ml-auto font-nunito text-xs text-meta">
            As of{' '}
            {new Date(data.generated_at).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatTile
          label="Unique visitors"
          value={data.totals.visitors}
          points={visitors}
          footnote={range === '90d' ? MONTHLY_FOOTNOTE : undefined}
        />
        <StatTile label="Sessions" value={data.totals.visits} points={visitors} />
        <StatTile label="Page views" value={data.totals.pageviews} points={pageviews} />
        <StatTile label="Blog to enquiry" value={percent(data.funnel.overall_rate)} />
      </div>

      <TrendChart points={data.trend} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FunnelChart funnel={data.funnel} />
        <TopArticlesTable articles={data.top_articles} />
      </div>
    </div>
  );
}
